import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import { useAppSelector } from '../../redux/hooks';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ClinicLocationService } from '../../services/clinic-location.service';
import { DailyDataRange } from '../UI/organisms/OvUserCharts';
import queryString from 'query-string';
import { endOfDay, startOfDay } from 'date-fns';
import { useClinicLocationPatient } from '../../hooks/use-clinic-location-patient';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { PatientDetailTabType } from '../../common/model/type/patient-detail-tab.type';
import { last } from 'lodash';
import OvPatientDetailsHeader from '../UI/organisms/OvPatientDetailsHeader';
import OvPatientInfo from '../UI/organisms/OvPatientInfo';
import OvUserDetailReports from '../UI/organisms/OvUserDetailReports';
import OvPatientInfoCalendar from '../UI/organisms/OvPatientInfoCalendar';
import FullScript from './FullScript';
import OvUserCharts from '../UI/organisms/OvUserCharts';
import {
  getPatientCycleHormoneSummary,
  getPatientCycles,
} from '../../services/clinic-location-clinical-data.service';
// Hidden per ops feedback (2026-08-18) — feature is functionally complete but not ready to
// ship; may return as-is or reworked. Do not delete.
// import OvSimilarPatients from '../UI/organisms/OvSimilarPatients';

const PatientDetails: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const url = useRouteUrl();
  const { patientId }: any = useParams();
  const selectedClinicLocation: ClinicLocation | undefined = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const queryClient = useQueryClient();
  const {
    data: selectedPatient,
    isPending: isPatientPending,
    isFetching: isPatientFetching,
  } = useClinicLocationPatient(selectedClinicLocation?.id, patientId);
  const isClinicLocationLoading = isPatientPending || isPatientFetching;
  const isFullScriptLinked = !!useAppSelector(
    (state) => state.user.currentUser?.fullscript
  );
  const fsTempAuthCode = queryString.parseUrl(search)?.query?.code as string;

  const [activeTab, setActiveTab] = useState<PatientDetailTabType>(
    PatientDetailTabType.HORMONE_CHARTS
  );
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const selectedPatientDocumentId = selectedPatient?.document_id;

  useEffect(() => {
    const setActiveTabFromUrl = () => {
      const pathName = last(
        location.pathname.split('/')
      ) as PatientDetailTabType;
      const {
        HORMONE_CHARTS,
        PATIENT_INFO,
        PATIENT_REPORTS,
        PATIENT_CALENDAR,
        RECOMMENDATIONS,
      } = PatientDetailTabType;

      if (
        [
          HORMONE_CHARTS,
          PATIENT_INFO,
          PATIENT_REPORTS,
          PATIENT_CALENDAR,
          RECOMMENDATIONS,
        ].includes(pathName)
      ) {
        setActiveTab(pathName);
      }
    };

    setActiveTabFromUrl();
  }, [selectedPatient?.document_id, location.pathname]);

  const changeTab = (event: any, newValue: PatientDetailTabType) => {
    setActiveTab(newValue);
  };

  // FullScript persists the link/unlink server-side itself; refetch the patient so the
  // updated fullscript_patient_id is reflected (replaces the legacy changeFullscriptPatientId).
  const invalidatePatient = () =>
    queryClient.invalidateQueries({
      queryKey: [
        'clinic-location-patient',
        selectedClinicLocation?.id,
        patientId,
      ],
    });

  const handleLinkFsToPatient = () => {
    invalidatePatient();
  };

  const handleUnLinkFsToPatient = () => {
    invalidatePatient();
  };

  const fetchPatientDailyData = async ({
    startDate,
    endDate,
  }: DailyDataRange) => {
    const query = queryString.stringify({
      limit: 0,
      user_document_id: selectedPatientDocumentId,
      day_from: startOfDay(startDate).toISOString(),
      day_to: endOfDay(endDate).toISOString(),
    });

    const paginated = await ClinicLocationService.getPatientDailyData(
      selectedClinicLocation?.id ?? '',
      selectedPatientDocumentId ?? '',
      query
    );

    return paginated.docs;
  };

  const fetchPatientCalendarDailyData = async ({
    startDate,
    endDate,
  }: DailyDataRange) => {
    const query = queryString.stringify({
      order_by: 'day',
      order_dir: 'asc',
      user_document_id: selectedPatientDocumentId,
      day_from: startDate.toISOString(),
      day_to: endDate.toISOString(),
      limit: 100,
    });

    const paginated = await ClinicLocationService.getPatientDailyData(
      selectedClinicLocation?.id ?? '',
      selectedPatientDocumentId ?? '',
      query
    );

    return paginated.docs;
  };

  return (
    <DetailPageWrapper>
      {!isClinicLocationLoading &&
        selectedPatient &&
        selectedClinicLocation && (
          <>
            <OvPatientDetailsHeader patient={selectedPatient} />

            {selectedPatient?.is_demo_account && (
              <StyledAlert severity="warning">
                <AlertTitle>{t('userDetails.demoTitle')}</AlertTitle>
                {t('userDetails.demoDesc')}
              </StyledAlert>
            )}

            <TabContainer>
              <Tabs onChange={changeTab} value={activeTab}>
                <Tab
                  style={{
                    borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                    textTransform: 'none',
                  }}
                  value={PatientDetailTabType.HORMONE_CHARTS}
                  label={t('dashboard.tabs.hormoneCharts')}
                  component={Link}
                  to={`${url}/${PatientDetailTabType.HORMONE_CHARTS}`}
                  replace
                />
                <Tab
                  style={{
                    borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                    textTransform: 'none',
                  }}
                  value={PatientDetailTabType.PATIENT_INFO}
                  label={t('dashboard.tabs.patientInfo')}
                  component={Link}
                  to={`${url}/${PatientDetailTabType.PATIENT_INFO}`}
                  replace
                />
                <Tab
                  style={{
                    borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                    textTransform: 'none',
                  }}
                  value={PatientDetailTabType.PATIENT_REPORTS}
                  label={t('dashboard.tabs.reports')}
                  component={Link}
                  to={`${url}/${PatientDetailTabType.PATIENT_REPORTS}`}
                  replace
                />
                <Tab
                  style={{
                    borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                    textTransform: 'none',
                  }}
                  value={PatientDetailTabType.PATIENT_CALENDAR}
                  label={t('dashboard.tabs.calendar')}
                  component={Link}
                  to={`${url}/${PatientDetailTabType.PATIENT_CALENDAR}`}
                  replace
                />
                {!!(isFullScriptLinked || fsTempAuthCode) && (
                  <Tab
                    style={{
                      borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                      textTransform: 'none',
                    }}
                    value={PatientDetailTabType.RECOMMENDATIONS}
                    label={t('common.recommendations')}
                    component={Link}
                    to={`${url}/${PatientDetailTabType.RECOMMENDATIONS}`}
                    replace
                  />
                )}
              </Tabs>
            </TabContainer>
            <Routes>
              <Route
                index
                element={<Navigate to={`${url}/hormone-charts`} replace />}
              />
              <Route
                path="hormone-charts/*"
                element={
                  <ContentWrapper>
                    <OvUserCharts
                      user={selectedPatient}
                      fetchDailyData={fetchPatientDailyData}
                      enabled={!!selectedPatientDocumentId}
                      dailyDataQueryKey={[
                        'clinic-location-patient-daily-data',
                        selectedClinicLocation?.id,
                        selectedPatientDocumentId,
                        'charts',
                      ]}
                      fetchCalendarDailyData={fetchPatientCalendarDailyData}
                      calendarDailyDataQueryKey={[
                        'clinic-location-patient-daily-data-range',
                        selectedClinicLocation?.id,
                        selectedPatientDocumentId,
                      ]}
                      fetchCycles={(query) =>
                        getPatientCycles(
                          selectedClinicLocation.id,
                          selectedPatient.id,
                          query
                        )
                      }
                      fetchCycleHormoneSummary={(cycleId) =>
                        getPatientCycleHormoneSummary(
                          selectedClinicLocation.id,
                          selectedPatient.id,
                          cycleId
                        )
                      }
                      cycleQueryKey={[
                        'clinic-location-patient-cycles',
                        selectedClinicLocation?.id,
                        selectedPatientDocumentId,
                      ]}
                    />
                    {/*
                    Hidden per ops feedback (2026-08-18) — feature is functionally complete but not ready to
                    ship; may return as-is or reworked. Do not delete.
                  */}
                    {/* <OvSimilarPatients
                    clinicLocationId={selectedClinicLocation.id}
                    patientId={selectedPatient.id}
                  /> */}
                  </ContentWrapper>
                }
              />
              <Route
                path="patient-info/*"
                element={
                  <ContentWrapper>
                    <OvPatientInfo selectedPatient={selectedPatient} />
                  </ContentWrapper>
                }
              />
              <Route
                path="reports/*"
                element={
                  <OvUserDetailReports
                    selectedUser={selectedPatient}
                    clinicLocationId={selectedClinicLocation.id}
                    currentUser={currentUser}
                  />
                }
              />
              <Route
                path="calendar/*"
                element={
                  <OvPatientInfoCalendar
                    selectedPatient={selectedPatient}
                    fetchDailyData={fetchPatientDailyData}
                    dailyDataQueryKey={[
                      'clinic-location-patient-daily-data',
                      selectedClinicLocation?.id,
                      selectedPatientDocumentId,
                      'calendar',
                    ]}
                  />
                }
              />
              <Route
                path="recommendations/*"
                element={
                  <FullScript
                    patient={selectedPatient}
                    onLinkToPatient={handleLinkFsToPatient}
                    onUnlinkToPatient={handleUnLinkFsToPatient}
                  />
                }
              />
            </Routes>
          </>
        )}
      {isClinicLocationLoading && <OvLoadingIndicator position="fixed" />}
    </DetailPageWrapper>
  );
};

export default PatientDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
`;

const TabContainer = styled.div`
  margin-bottom: 1.5rem;
  margin-right: 0.5rem;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;

  && {
    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const StyledAlert = styled(Alert)`
  && {
    margin: 0.25rem 0 0.5rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
